/**
 * Fee Calculator Utility
 *
 * Client-side calculations for live fee previews in FI configuration
 * and merchant forms. These mirror the backend RevenueEngine calculations.
 *
 * Revenue Model (Share-of-Net):
 * - Solvent Buy Rate = (Transaction × solvent_buy_rate_pct) + solvent_buy_rate_usd
 *   This is Solvent's guaranteed minimum per transaction.
 * - Markup = Total Fees - Solvent Buy Rate
 * - ISO Revenue = Markup × reseller_split_pct
 * - Solvent Revenue = Solvent Buy Rate + (Markup × (1 - reseller_split_pct))
 *
 * Note: Buffer is applied to the crypto exchange rate, not as a fee.
 * Hard costs (exchange, bank, solvent fee) are tracked for accounting but
 * don't affect fee collection - they're covered by Solvent's buy rate.
 */

/**
 * Calculate merchant fee breakdown for a transaction
 *
 * @param {Object} params
 * @param {number} params.transactionAmount - Transaction amount in USD
 * @param {number} params.merchantFeePct - Merchant fee percentage
 * @param {number} params.customerFeePct - Customer fee percentage
 * @param {number} [params.solventFeePct=0.5] - Solvent fee percentage (tracked for accounting)
 * @param {number} [params.exchangeFeePct=0.25] - Exchange fee percentage (tracked for accounting)
 * @param {number} [params.bankFeePct=0.25] - Bank fee percentage (tracked for accounting)
 * @returns {Object} Fee breakdown
 */
export function calculateFeeBreakdown({
  transactionAmount,
  merchantFeePct,
  customerFeePct,
  solventFeePct = 0.5,
  exchangeFeePct = 0.25,
  bankFeePct = 0.25,
}) {
  const amount = parseFloat(transactionAmount) || 0;
  const merchantFee = amount * (parseFloat(merchantFeePct) || 0) / 100;
  const customerFee = amount * (parseFloat(customerFeePct) || 0) / 100;

  // Total fees collected (what's available for revenue split)
  const totalFees = merchantFee + customerFee;

  // Hard costs (tracked for accounting, covered by Solvent's buy rate)
  const solventFee = amount * (parseFloat(solventFeePct) || 0) / 100;
  const exchangeFee = amount * (parseFloat(exchangeFeePct) || 0) / 100;
  const bankFee = amount * (parseFloat(bankFeePct) || 0) / 100;
  const hardCosts = solventFee + exchangeFee + bankFee;

  return {
    transactionAmount: round2(amount),
    merchantFee: round2(merchantFee),
    customerFee: round2(customerFee),
    totalFees: round2(totalFees),
    // Hard costs (for accounting, don't affect revenue split)
    solventFee: round2(solventFee),
    exchangeFee: round2(exchangeFee),
    bankFee: round2(bankFee),
    hardCosts: round2(hardCosts),
    // For compatibility with existing code
    grossFees: round2(totalFees),
    netRevenue: round2(totalFees),
  };
}

/**
 * Calculate revenue split between ISO and Solvent
 *
 * New Model (Share-of-Net):
 * - Solvent Buy Rate = (Transaction × solvent_buy_rate_pct) + solvent_buy_rate_usd
 *   This is Solvent's GUARANTEED minimum per transaction.
 * - Markup = Total Fees - Solvent Buy Rate
 * - ISO Revenue = Markup × reseller_split_pct (ISO gets this % of markup)
 * - Solvent Revenue = Solvent Buy Rate + (Markup × (1 - reseller_split_pct))
 *
 * @param {Object} params
 * @param {number} params.transactionAmount - Transaction amount in USD
 * @param {number} params.grossFees - Total fees collected (merchant_fee + customer_fee)
 * @param {number} [params.hardCosts=0] - Hard costs (for display, doesn't affect split)
 * @param {number} params.solventBuyRatePct - Solvent's guaranteed percentage
 * @param {number} [params.solventBuyRateUsd=0] - Solvent's guaranteed flat fee
 * @param {number} params.resellerSplitPct - ISO's share of markup percentage
 * @returns {Object} Revenue split breakdown
 */
export function calculateRevenueSplit({
  transactionAmount,
  grossFees,
  hardCosts = 0,
  // New field names (Solvent buy rate)
  solventBuyRatePct,
  solventBuyRateUsd = 0,
  resellerSplitPct,
  // Legacy support for old prop names
  buyRatePct,
  buyRateUsd,
  resellerSplitUsd, // Ignored in new model
}) {
  const txAmount = parseFloat(transactionAmount) || 0;
  const totalFees = parseFloat(grossFees) || 0;
  const costs = parseFloat(hardCosts) || 0;

  // Support both old and new prop names
  const buyPct = parseFloat(solventBuyRatePct ?? buyRatePct) || 0;
  const buyUsd = parseFloat(solventBuyRateUsd ?? buyRateUsd) || 0;
  const splitPct = parseFloat(resellerSplitPct) || 0;

  // Step 1: Calculate Solvent's buy rate (guaranteed minimum to Solvent)
  const solventBuyRatePctAmount = txAmount * buyPct / 100;
  const solventBuyRateAmount = solventBuyRatePctAmount + buyUsd;

  // Step 2: Calculate markup (fees above Solvent's buy rate)
  const markup = totalFees - solventBuyRateAmount;

  // Step 3: Calculate ISO's share of markup
  // ISO Revenue = Markup × reseller_split_pct
  const isoShareOfMarkup = markup * splitPct / 100;

  // Step 4: Calculate final revenue split
  // ISO gets: their share of markup
  // Solvent gets: buy rate + remaining markup
  const isoRevenue = isoShareOfMarkup;
  const solventRevenue = solventBuyRateAmount + (markup - isoShareOfMarkup);

  // Calculate percentages of total fees
  const fiPctOfGross = totalFees > 0 ? (isoRevenue / totalFees) * 100 : 0;
  const solventPctOfGross = totalFees > 0 ? (solventRevenue / totalFees) * 100 : 0;

  return {
    // Inputs
    transactionAmount: round2(txAmount),
    grossFees: round2(totalFees),
    totalFees: round2(totalFees),
    hardCosts: round2(costs),
    solventBuyRatePct: round2(buyPct),
    solventBuyRateUsd: round2(buyUsd),
    resellerSplitPct: round2(splitPct),
    // Legacy field names for compatibility
    buyRatePct: round2(buyPct),
    buyRateUsd: round2(buyUsd),

    // Calculated values
    netRevenue: round2(totalFees), // In new model, net = total fees
    solventBuyRatePctAmount: round2(solventBuyRatePctAmount),
    solventBuyRateAmount: round2(solventBuyRateAmount),
    buyRateAmount: round2(solventBuyRateAmount), // Legacy alias
    markup: round2(markup),
    spreadAboveBuyRate: round2(markup), // Legacy alias

    // Revenue split
    isoShareOfMarkup: round2(isoShareOfMarkup),
    fiShareOfSpread: round2(isoShareOfMarkup), // Legacy alias
    fiRevenue: round2(isoRevenue),
    isoRevenue: round2(isoRevenue),
    solventRevenue: round2(solventRevenue),

    // Percentages
    fiPctOfGross: round2(fiPctOfGross),
    isoPctOfGross: round2(fiPctOfGross),
    solventPctOfGross: round2(solventPctOfGross),

    // Status flags
    isNegativeSpread: markup < 0,
    isNegativeMarkup: markup < 0,
    isSolventNegative: solventRevenue < 0,
  };
}

/**
 * Generate example transaction for preview
 *
 * Uses typical fee values to show how revenue would be split.
 *
 * @param {number} amount - Transaction amount (default: 100)
 * @returns {Object} Example fee breakdown with typical values
 */
export function generateExampleTransaction(amount = 100) {
  return calculateFeeBreakdown({
    transactionAmount: amount,
    merchantFeePct: 3.0,  // Merchant pays 3%
    customerFeePct: 1.5,  // Customer pays 1.5%
    solventFeePct: 0.5,   // Solvent fee (tracked)
    exchangeFeePct: 0.25, // Exchange fee (tracked)
    bankFeePct: 0.25,     // Bank fee (tracked)
  });
}

/**
 * Round to 2 decimal places
 * @param {number} value
 * @returns {number}
 */
function round2(value) {
  return Math.round(value * 100) / 100;
}

/**
 * Format currency for display
 * @param {number} value
 * @param {boolean} [showSign=false] - Show + for positive values
 * @returns {string}
 */
export function formatCurrency(value, showSign = false) {
  const num = parseFloat(value) || 0;
  const formatted = Math.abs(num).toFixed(2);
  if (num < 0) {
    return `-$${formatted}`;
  }
  if (showSign && num > 0) {
    return `+$${formatted}`;
  }
  return `$${formatted}`;
}

/**
 * Format percentage for display
 * @param {number} value
 * @returns {string}
 */
export function formatPercentage(value) {
  const num = parseFloat(value) || 0;
  return `${num.toFixed(2)}%`;
}

/**
 * Format buy rate or reseller split label showing both % and USD if applicable
 * @param {number} pct - Percentage value
 * @param {number} usd - Flat USD value
 * @param {string} [suffix=''] - Optional suffix (e.g., ' to FI')
 * @returns {string}
 */
export function formatRateLabel(pct, usd, suffix = '') {
  const pctVal = parseFloat(pct) || 0;
  const usdVal = parseFloat(usd) || 0;

  if (usdVal > 0) {
    return `${pctVal.toFixed(2)}% + $${usdVal.toFixed(2)}${suffix}`;
  }
  return `${pctVal.toFixed(2)}%${suffix}`;
}
